import { Button, Label, TableItem } from 'components/atoms';
import { FormInput, MagellanPopups } from 'components/molecules';
import { QueryFilter, Table, TopBar } from 'components/organisms';
import { TopbarPage } from 'components/templates';
import { FormikHelpers } from 'formik';
import { useComponentVisible } from 'hooks';
import { useInfiniteTable } from 'hooks/useInfiniteTable';
import { useParamId } from 'hooks/useParamId';
import { useTranslation } from 'react-i18next';
import {
  PiEye,
  PiNavigationArrow,
  PiPlay,
  PiThumbsDown,
  PiThumbsUp,
  PiVideo,
} from 'react-icons/pi';
import { PopupActions } from 'reactjs-popup/dist/types';
import {
  useGetChannelAllVideosInfinite,
  usePostChannelVideoById,
} from 'services/channel';
import {
  IAddVideoById,
  IChannelVideoSearch,
  VIDEO_TYPE,
} from 'services/channel/types/query';
import { useSearchById, useSetSearchById } from 'store/state';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { useEnumToSelectOptions } from 'utils/components/inputs/select';
import { object, string } from 'yup';
import BulkAssignVideoContractsPopup from './BulkAssignVideoContractsPopup';
export const getTableId = (id: string) => `channel-video-${id}`;
const ChannelVideo = () => {
  const channelId = useParamId('channel');
  const tableId = getTableId(channelId);
  const { ref, isComponentVisible, handleComponentVisible } =
    useComponentVisible<HTMLDivElement>(false, true, tableId);
  const filtersVisible = useComponentVisible<HTMLDivElement>(
    false,
    true,
    tableId
  );

  const searchById = useSearchById();
  const setSearchById = useSetSearchById();
  const filters = searchById[tableId] || {
    videoTitle: '',
    assetId: '',
    externalVideoId: '',
    videoType: undefined,
  };

  const getVideos = useGetChannelAllVideosInfinite({ ...filters, channelId });

  const addVideo = usePostChannelVideoById(channelId);

  const tableProps = useInfiniteTable(getVideos);

  const { t } = useTranslation();

  const i18nextPrefix = 'channel.video';

  const navigateToYtVideo = (videoId: string) => {
    const targetPath = `https://www.youtube.com/watch?v=${videoId}`;
    window.open(targetPath, '_blank');
  };

  const onAddVideo = async (
    values: IAddVideoById,
    formikHelpers: FormikHelpers<IAddVideoById>,
    actions: PopupActions | null
  ) =>
    await addVideo.mutateAsync(values, {
      onSuccess: () => {
        getVideos.refetch();
        actions?.close();
      },
    });

  const { getSelectOptions } = useEnumToSelectOptions();

  return (
    <TopbarPage
      topbar={
        <TopBar
          size='compact'
          hideHeader
          topbarContent={
            <div className='flex gap-2'>
              <QueryFilter.ButtonOpener
                i18nextPrefix='statements'
                onClick={handleComponentVisible}
                variant={BUTTON_VARIANT.default}
                isClear
              />
              <BulkAssignVideoContractsPopup />
              <MagellanPopups.Form
                header={
                  <>
                    <PiVideo /> {t('channel.popup.input.addByVideoId')}
                  </>
                }
                validationSchema={object().shape({
                  videoId: string().required(t('formik-error.required')),
                })}
                initialValues={{ videoId: '' }}
                onSubmit={onAddVideo}
                trigger={
                  <Button type='button'>
                    <Button.Text text={t('button.add')} />
                  </Button>
                }>
                <FormInput.Text
                  type='text'
                  label={t(`channel.popup.input.byVideoId`)}
                  placeholder='PUwfdhRc5jYswE'
                  name={'videoId'}
                />
              </MagellanPopups.Form>
            </div>
          }>
          <QueryFilter<IChannelVideoSearch>
            i18nextPrefix='statements'
            initialValues={filters}>
            <>
              <QueryFilter.Form<IChannelVideoSearch>
                ref={ref}
                isOpen={isComponentVisible}
                onDebouncedFormChange={values =>
                  setSearchById(tableId, values)
                }>
                <QueryFilter.InputsGrid>
                  <FormInput.Select
                    name='videoType'
                    label={t('input.label.videoType')}
                    options={getSelectOptions(VIDEO_TYPE, 'enum.VIDEO_TYPE')}
                  />
                  <FormInput.Text
                    name='videoTitle'
                    label={t('input.label.title')}
                  />
                  <FormInput.Text
                    name='externalVideoId'
                    label={t('input.label.externalVideoId')}
                  />
                  <FormInput.Text
                    name='assetId'
                    label={t('input.label.assetId')}
                  />
                </QueryFilter.InputsGrid>
              </QueryFilter.Form>
              <QueryFilter.Labels
                isFormOpen={isComponentVisible}
                customLabel={data =>
                  data.key === 'videoType'
                    ? t(`enum.VIDEO_TYPE.${VIDEO_TYPE[data.value as VIDEO_TYPE]}`)
                    : `${data.value}`
                }
              />
            </>
          </QueryFilter>
        </TopBar>
      }>
      <Table
        id={'ChannelVideo'}
        size='compact'
        {...tableProps}
        data={{
          nodes: tableProps.data.nodes.map(v => ({
            ...v,
            id: `${v.id}/player`,
          })),
        }}
        columns={[
          {
            label: `${t(`${i18nextPrefix}.table.videoTitle`)}`,
            renderCell: ({ videoTitle, videoImageLink, videoType }) => (
              <TableItem>
                <div className='flex flex-row items-center gap-4'>
                  <img
                    src={videoImageLink}
                    alt=''
                    style={{
                      width: '80px',
                    }}
                  />
                  <TableItem.Text text={videoTitle} />
                </div>
              </TableItem>
            ),
            resize: false,
          },

          {
            label: '',
            renderCell: ({
              externalVideoId,
              dislikesCount,
              likesCount,
              views,
              videoType,
            }) => (
              <TableItem justifyPosition='end'>
                <div className='flex gap-2 items-center'>
                  {videoType.id === VIDEO_TYPE.shorts && (
                    <Label color='red' size='compact' withText>
                      <PiPlay size={12} />
                      SHORTS
                    </Label>
                  )}
                  <Label
                    title='Views'
                    size='compact'
                    withText
                    style={{ minWidth: 120 }}
                    color='blue'>
                    <PiEye /> {views.toLocaleString()}
                  </Label>
                  <Label
                    title='Likes'
                    size='compact'
                    withText
                    style={{ minWidth: 100 }}>
                    <PiThumbsUp /> {likesCount.toLocaleString()}
                  </Label>
                  {!!dislikesCount && (
                    <Label size='compact' withText style={{ minWidth: 100 }}>
                      <PiThumbsDown /> {dislikesCount.toLocaleString()}
                    </Label>
                  )}
                  <Button
                    title='Dislikes'
                    type='button'
                    isClear
                    onlyIcon
                    onClick={() => navigateToYtVideo(externalVideoId)}>
                                        <Button.Description description={t('button.open')} />

                                        <Button.Icon
                      icon={<PiNavigationArrow transform='scale(-1,1)' />}
                    />
                  </Button>
                </div>
              </TableItem>
            ),
          },

          // {
          //   label: t(`${i18nextPrefix}.table.externalVideoId`),
          //   renderCell: ({ externalVideoId }) => (
          //     <TableItem>
          //       <TableItem.Text isSupplementary text={externalVideoId} />
          //     </TableItem>
          //   ),
          //   resize: true,
          // },
        ]}
        displayMode
      />
    </TopbarPage>
  );
};

export default ChannelVideo;
